// @file Pointing state for the EraserTool
// Original file: https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/tools/EraserTool/childStates/Pointing.ts

import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import type { TLEventHandlers, TLFrameShape, TLGroupShape, TLPointerEventInfo, TLShapeId } from '@tldraw/editor'
import { StateNode } from '@tldraw/editor'

export class Pointing extends StateNode {
  static override id = 'pointing'

  override onEnter = (): void => {
    const zoomLevel = this.editor.getZoomLevel()
    const currentPageShapesSorted = this.editor.getCurrentPageRenderingShapesSorted()
    const {
      inputs: { currentPagePoint },
    } = this.editor

    const erasing = new Set<TLShapeId>()

    const initialSize = erasing.size

    for (let n = currentPageShapesSorted.length, i = n - 1; i >= 0; i--) {
      const shape = currentPageShapesSorted[i]
      // Unique Padlet logic:
      // Eraser only works on `draw` and `highlight` shapes
      if (!['draw', 'highlight'].includes(shape.type)) continue
      // Unique Padlet logic:
      // If the user can't edit, we shouldn't erase shapes they didn't create
      if (
        !useWhiteboardPermissionsStore().canIEditFrame(this.editor.getCurrentPageId()) &&
        shape.meta.cb !== useSurfaceStore().user.id
      )
        continue
      // If the shape is locked, we shouldn't erase it
      if (this.editor.isShapeOrAncestorLocked(shape) || this.editor.isShapeOfType<TLGroupShape>(shape, 'group')) {
        continue
      }

      if (
        this.editor.isPointInShape(shape, currentPagePoint, {
          hitInside: false,
          margin: this.editor.options.hitTestMargin / zoomLevel,
        })
      ) {
        const hitShape = this.editor.getOutermostSelectableShape(shape)
        // If we've hit a frame after hitting any other shape, stop here
        if (this.editor.isShapeOfType<TLFrameShape>(hitShape, 'frame') && erasing.size > initialSize) {
          break
        }

        erasing.add(hitShape.id)
      }
    }

    this.editor.setErasingShapes([...erasing])
  }

  override onLongPress: TLEventHandlers['onLongPress'] = (info) => {
    this.startErasing(info)
  }

  override onExit = (_info: any, to: string): void => {
    if (to !== 'erasing') {
      this.editor.setErasingShapes([])
    }
  }

  override onPointerMove: TLEventHandlers['onPointerMove'] = (info) => {
    if (this.editor.inputs.isDragging) {
      this.startErasing(info)
    }
  }

  override onPointerUp: TLEventHandlers['onPointerUp'] = () => {
    this.complete()
  }

  override onCancel: TLEventHandlers['onCancel'] = () => {
    this.cancel()
  }

  override onComplete: TLEventHandlers['onComplete'] = () => {
    this.complete()
  }

  override onInterrupt: TLEventHandlers['onInterrupt'] = () => {
    this.cancel()
  }

  private startErasing(info: TLPointerEventInfo): void {
    this.parent.transition('erasing', info)
  }

  complete(): void {
    const erasingShapeIds = this.editor.getErasingShapeIds()

    if (erasingShapeIds.length > 0) {
      this.editor.markHistoryStoppingPoint('erase end')
      this.editor.deleteShapes(erasingShapeIds)
    }

    this.parent.transition('idle')
  }

  cancel(): void {
    this.parent.transition('idle')
  }
}
